const TRUE_VALUES = new Set(['1', 'true', 'yes', 'on'])
const FALSE_VALUES = new Set(['0', 'false', 'no', 'off'])

export const DEFAULT_SENTRY_SAMPLE_RATE = 1.0
const DEFAULT_SENTRY_FREE_TIER_SAMPLE_RATE = 0.1

function normalizeEnvValue(value: string | undefined): string | undefined {
  return value?.trim().toLowerCase()
}

function parseBooleanEnv(value: string | undefined, defaultValue: boolean): boolean {
  const normalized = normalizeEnvValue(value)
  if (!normalized) return defaultValue
  if (TRUE_VALUES.has(normalized)) return true
  if (FALSE_VALUES.has(normalized)) return false
  return defaultValue
}

export function parseSampleRate(value: string | undefined, defaultValue: number = DEFAULT_SENTRY_SAMPLE_RATE): number {
  if (!value) return defaultValue
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return defaultValue
  if (parsed < 0 || parsed > 1) return defaultValue
  return parsed
}

interface NodeProfilingConfig {
  tracesSampleRate: number
  profileSessionSampleRate: number
  profileLifecycle: 'trace'
  enableNodeProfiling: boolean
}

interface BrowserProfilingConfig {
  tracesSampleRate: number
  profileSessionSampleRate: number
  profileLifecycle: 'trace'
  enableBrowserProfiling: boolean
  enableBrowserProfilingHeader: boolean
}

function getDefaultTraceSampleRate(env: NodeJS.ProcessEnv): number {
  const appEnv = (env.VITE_APP_ENVIRONMENT ?? env.NODE_ENV ?? '').toLowerCase()
  return appEnv === 'development' || appEnv === 'dev'
    ? DEFAULT_SENTRY_SAMPLE_RATE
    : DEFAULT_SENTRY_FREE_TIER_SAMPLE_RATE
}

export function getNodeProfilingConfig(env: NodeJS.ProcessEnv): NodeProfilingConfig {
  const enableNodeProfiling = parseBooleanEnv(env.SENTRY_ENABLE_NODE_PROFILING, false)
  const tracesSampleRate = parseSampleRate(env.SENTRY_TRACES_SAMPLE_RATE, getDefaultTraceSampleRate(env))
  const configuredProfileRate = parseSampleRate(
    env.SENTRY_PROFILE_SESSION_SAMPLE_RATE,
    DEFAULT_SENTRY_FREE_TIER_SAMPLE_RATE,
  )
  const profileSessionSampleRate = enableNodeProfiling ? configuredProfileRate : 0

  return {
    tracesSampleRate,
    profileSessionSampleRate,
    profileLifecycle: 'trace',
    enableNodeProfiling,
  }
}

export function getBrowserProfilingConfig(env: NodeJS.ProcessEnv): BrowserProfilingConfig {
  const enableBrowserProfiling = parseBooleanEnv(env.VITE_SENTRY_ENABLE_BROWSER_PROFILING, false)
  const tracesSampleRate = parseSampleRate(env.VITE_SENTRY_TRACES_SAMPLE_RATE, getDefaultTraceSampleRate(env))
  const configuredProfileRate = parseSampleRate(
    env.VITE_SENTRY_PROFILE_SESSION_SAMPLE_RATE,
    DEFAULT_SENTRY_FREE_TIER_SAMPLE_RATE,
  )
  const profileSessionSampleRate = enableBrowserProfiling ? configuredProfileRate : 0
  const enableBrowserProfilingHeader = enableBrowserProfiling && profileSessionSampleRate > 0

  return {
    tracesSampleRate,
    profileSessionSampleRate,
    profileLifecycle: 'trace',
    enableBrowserProfiling,
    enableBrowserProfilingHeader,
  }
}
